<template>
  <div class="video-card bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-105 flex flex-col h-full">
    <!-- Video Thumbnail -->
    <div class="relative group cursor-pointer" @click="openVideo">
      <img 
        :src="thumbnailSrc" 
        :alt="video.title"
        class="w-full h-48 object-cover"
        loading="lazy"
        @error="handleImageError"
        @load="handleImageLoad"
      />
      <!-- Play button overlay -->
      <div class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-50 transition-all duration-300">
        <div class="bg-black text-white rounded-full w-16 h-16 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
          <i class="fas fa-play text-xl ml-1 text-white"></i>
        </div>
      </div>
    </div>
    
    <!-- Video Info -->
    <div class="p-6 flex flex-col flex-grow">
      <h3 class="text-xl font-semibold mb-2 text-gray-900">
        {{ video.title }}
      </h3>
      
      <p class="text-gray-600 text-sm mb-4 line-clamp-3 flex-grow">
        {{ truncatedDescription }}
      </p>
      
      <!-- Published date -->
      <p v-if="video.publishedAt" class="text-xs text-gray-500 mb-4">
        {{ formatDate(video.publishedAt) }}
      </p>
      
      <!-- Action buttons - always at bottom -->
      <div class="flex space-x-3 mt-auto">
        <button 
          @click="openVideo" 
          class="flex-1 bg-black hover:bg-gray-800 text-white py-2 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
        >
          <i class="fab fa-youtube mr-2 text-white"></i>
          {{ $t('videos.card.watchOnYoutube') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { trackVideoEvent, trackExternalLink } from '@/utils/gtm-tracking'
import { videoCache } from '@/utils/video-cache'

export default {
  props: {
    video: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      thumbnailSrc: '',
      imageLoaded: false,
      imageError: false
    }
  },
  async mounted() {
    await this.setupThumbnail();
  },
  computed: {
    truncatedDescription() {
      if (!this.video.description) return '';
      
      // Truncate description to a reasonable length for card display
      const maxLength = 150;
      if (this.video.description.length <= maxLength) {
        return this.video.description;
      }
      
      return this.video.description.substring(0, maxLength) + '...';
    }
  },
  methods: {
    async setupThumbnail() {
      // If we already have a cached thumbnailUrl (base64), use it
      if (this.video.thumbnailUrl && this.video.thumbnailUrl.startsWith('data:')) {
        this.thumbnailSrc = this.video.thumbnailUrl;
        return;
      }
      
      // Check for cached image
      const youtubeId = this.video.youtubeId || this.extractYouTubeId();
      if (youtubeId) {
        const cachedImage = videoCache.getCachedImage(youtubeId);
        if (cachedImage) {
          this.thumbnailSrc = cachedImage;
          return;
        }
      }
      
      // Fallback to original URL or generate standard YouTube thumbnail
      this.thumbnailSrc = this.video.thumbnailUrl || this.generateThumbnailUrl(youtubeId);
    },
    
    extractYouTubeId() {
      if (this.video.id?.videoId) return this.video.id.videoId;
      if (typeof this.video.id === 'string') return this.video.id;
      if (this.video.youtubeId) return this.video.youtubeId;
      return null;
    },
    
    generateThumbnailUrl(youtubeId) {
      if (!youtubeId) return '/placeholder-video.svg';
      return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
    },
    
    async handleImageError() {
      console.warn('Failed to load thumbnail for video:', this.video.title);
      this.imageError = true;
      
      // Try to cache and use a fallback thumbnail
      const youtubeId = this.extractYouTubeId();
      if (youtubeId) {
        try {
          // Try different thumbnail qualities as fallback
          const fallbackUrls = [
            `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`,
            `https://img.youtube.com/vi/${youtubeId}/sddefault.jpg`,
            `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`,
            `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`,
            `https://img.youtube.com/vi/${youtubeId}/default.jpg`
          ];
          
          for (const url of fallbackUrls) {
            try {
              const cachedUrl = await videoCache.cacheImage(url, youtubeId);
              this.thumbnailSrc = cachedUrl;
              break;
            } catch (error) {
              console.warn('Fallback thumbnail failed:', url);
            }
          }
        } catch (error) {
          console.error('All thumbnail fallbacks failed:', error);
        }
      }
    },
    
    handleImageLoad() {
      this.imageLoaded = true;
      console.log('✅ Thumbnail loaded for:', this.video.title);
    },
    
    openVideo() {
      // Track video click event
      trackVideoEvent('click', this.video.title, this.video.youtubeId || this.extractYouTubeId())
      trackExternalLink('Watch on YouTube', `https://www.youtube.com/watch?v=${this.video.youtubeId || this.extractYouTubeId()}`)
      
      // Open YouTube video in a new tab
      const youtubeId = this.video.youtubeId || this.extractYouTubeId();
      const youtubeUrl = `https://www.youtube.com/watch?v=${youtubeId}`;
      window.open(youtubeUrl, '_blank');
    },
    formatDate(dateString) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      return date.toLocaleDateString(this.$i18n.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }
}
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.video-card:hover {
  transform: translateY(-5px);
}
</style>
